import { useEffect } from 'react';

import { TestSummaryBar } from 'pages/custom_tests/components/custom_test_wizard/components/test_summary_bar/TestSummaryBar';
import { EWizardMode, EWizardStep } from 'pages/custom_tests/components/custom_test_wizard/machine';
import type { QuestionItem } from 'pages/custom_tests/components/question_picker/QuestionPicker';
import { useCustomTestWizard } from 'pages/custom_tests/components/custom_test_wizard/context';
import { Alert, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { License } from 'apis/LicenseApi';
import { LicenseApi } from 'apis/LicenseApi';
import { useSuperState } from 'hooks';

// Application questions are identified by their resolved type name (numeric type id '1'
// as a fallback) — never by substring-matching the numeric QuestionType field.
const isApplicationQuestion = (question: QuestionItem): boolean => question.QuestionTypeName === 'Application' || question.QuestionType === '1';

export const SummaryStep = () => {

    const { name, categories, categoryId, editStep, languageId, languages, minScore, minutes, mode, modeId, orderMode,
        productIds, products, questionIds, questionTree, randomly, sbClassroomId, sbClassrooms, sbPurpose, sbScopeType,
        sbScopeValueIds, selectedQuestions, setProviderState, smartBuildRun, useRandomly } = useCustomTestWizard();
    const { formatMessage } = useIntl();

    // Required licenses for assignment: null = loading, [] = none grant the selected
    // programs. A failed lookup keeps the static licensing sentence as a fallback.
    const [state, setState] = useSuperState({
        licenses: null as License[] | null,
        licensesFailed: false,
        smsDismissed: false
    });

    const { licenses, licensesFailed, smsDismissed } = state;

    useEffect(() => {

        let cancelled = false;

        const loadLicenses = async () => {

            try {

                const result = await LicenseApi.GetLicensesByIncludeExclude('Product', productIds, []);
                if (!cancelled) setState({ licenses: result ?? [] });

            } catch {

                if (!cancelled) setState({ licensesFailed: true });

            }

        };

        loadLicenses();

        return () => {

            cancelled = true;

        };

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedSet = new Set(questionIds.split(',').map(s => s.trim()).filter(Boolean).map(Number));

    const selectedCategory = categories.find(({ Id }) => Id === categoryId);
    const selectedProducts = products.filter(({ Id }) => productIds.includes(Id));
    const selectedLanguage = languages.find(({ Id }) => Id === languageId);

    const modeLabel = modeId === EWizardMode.TRAINING
        ? (formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' }))
        : modeId === EWizardMode.BOTH
            ? (formatMessage({ id: 'both.8074bfd4', defaultMessage: 'Both' }))
            : (formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' }));

    const deliveredCount = useRandomly ? randomly : selectedQuestions;
    const deliveryLabel = useRandomly
        ? formatMessage({ id: 'random_subset_delivered_of_total_questio.5a3b9d00', defaultMessage: 'Random subset - {delivered} of {total} questions' }, { delivered: randomly, total: selectedQuestions })
        : (formatMessage({ id: 'all_selected_questions.a343dbda', defaultMessage: 'All selected questions' }));
    const orderLabel = orderMode === 'grouped'
        ? (formatMessage({ id: 'grouped_by_objective.1ea8241f', defaultMessage: 'Grouped by objective' }))
        : (formatMessage({ id: 'fully_randomized.3abbb070', defaultMessage: 'Fully randomized' }));

    const objectives = (questionTree.Products ?? [])
        .flatMap(product => product.Themes ?? [])
        .flatMap(theme => theme.Objectives ?? []);
    const allQuestions = objectives.flatMap(objective => objective.Questions ?? []);

    const objectiveMap = new Map<number, { id: number; name: string; count: number }>();
    objectives.forEach(objective => {

        const count = (objective.Questions ?? []).filter(q => selectedSet.has(q.QuestionId)).length;
        if (count === 0) return;

        const existing = objectiveMap.get(objective.ObjectiveId);
        if (existing) existing.count += count;
        else objectiveMap.set(objective.ObjectiveId, { id: objective.ObjectiveId, name: objective.Name, count });

    });
    const objectivesWithSelected = Array.from(objectiveMap.values());

    const sourceCount = new Set(allQuestions
        .filter(q => selectedSet.has(q.QuestionId))
        .map(q => (q as any).QuestionSource ?? q.Source ?? '')
        .filter(Boolean)).size;

    const hasQuestions = selectedQuestions > 0;

    // SMS requirement notice (unique selected questions): all-application tests need SMS;
    // mixing application and knowledge questions needs SMS 8.
    const selectedQuestionList = Array.from(
        new Map(allQuestions.filter(q => selectedSet.has(q.QuestionId)).map(q => [q.QuestionId, q])).values()
    );
    const hasApplication = selectedQuestionList.some(isApplicationQuestion);
    const hasNonApplication = selectedQuestionList.some(q => !isApplicationQuestion(q));

    // Smart Build recap: recomposes the saved statement (For [Classroom], build a test
    // to [Purpose] using content from [Scope] with [Size] questions) from the wizard state.
    const sbClassroomName = sbClassrooms.find(classroom => classroom.Id === sbClassroomId)?.Name ?? '';

    const sbPurposePhrases: Record<string, string> = {
        'coverage-gaps': formatMessage({ id: 'fill_coverage_gaps.b35fc5e2', defaultMessage: 'fill coverage gaps' }),
        mastery: formatMessage({ id: 'maintain_mastery.8f64bd59', defaultMessage: 'maintain mastery' }),
        readiness: formatMessage({ id: 'check_readiness.f82151be', defaultMessage: 'check readiness' }),
        'recent-activity': formatMessage({ id: 'review_recent_activity.47e8dd0f', defaultMessage: 'review recent activity' }),
        'selected-content': formatMessage({ id: 'review_selected_content.61c7ec70', defaultMessage: 'review selected content' }),
        'weak-skills': formatMessage({ id: 'strengthen_weak_skills.9cd8f36f', defaultMessage: 'strengthen weak skills' })
    };

    const sbScopePhrase = sbScopeType === 'topic' || sbScopeType === 'objective'
        ? `${sbScopeValueIds.length} ${sbScopeValueIds.length === 1
            ? (sbScopeType === 'topic' ? (formatMessage({ id: 'selected_topic.ddaf58ae', defaultMessage: 'selected topic' })) : (formatMessage({ id: 'selected_objective.ac0d48e6', defaultMessage: 'selected objective' })))
            : (sbScopeType === 'topic' ? (formatMessage({ id: 'selected_topics.8d28b7f8', defaultMessage: 'selected topics' })) : (formatMessage({ id: 'selected_objectives.ff139b20', defaultMessage: 'selected objectives' })))}`
        : (formatMessage({ id: 'all_available_content.2dd8d518', defaultMessage: 'all available content' }));

    const sbStatement = formatMessage(
        { id: 'for_classroom_build_a_test_to_purpose_us.86383d45', defaultMessage: 'For {classroom}, build a test to {purpose} using content from {scope} with {count} questions.' },
        {
            classroom: sbClassroomName,
            count: selectedQuestions,
            purpose: sbPurposePhrases[sbPurpose] ?? '',
            scope: sbScopePhrase
        }
    );

    const renderSectionHeader = (title: string, onEdit: () => void) => (
        <div className={'d-flex justify-content-between align-items-center mb-2'}>
            <h6 className={'fw-bold text-primary mb-0'}>
                {title}
            </h6>
            <button
                className={'btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1'}
                onClick={onEdit}
                type={'button'}
            >
                <i className={'fa-regular fa-pen-to-square'} />
                <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
            </button>
        </div>
    );

    const renderSetting = (label: string, value: string) => (
        <Col md={6} xs={12}>
            <Row className={'gx-2'}>
                <Col className={'text-muted small'} xs={5}>
                    {label}
                </Col>
                <Col xs={7}>
                    {value}
                </Col>
            </Row>
        </Col>
    );

    const renderStat = (value: string | number, label: string) => (
        <Col md={3} xs={6}>
            <div className={'border rounded bg-light px-3 py-2 h-100'}>
                <div className={'h5 fw-bold mb-0'}>
                    {value}
                </div>
                <div className={'small text-muted'}>
                    {label}
                </div>
            </div>
        </Col>
    );

    return (
        <div className={'d-flex flex-column gap-4 py-2'}>

            {hasApplication && !smsDismissed && (
                <Alert
                    dismissible
                    className={'mb-0 py-2 small'}
                    onClose={() => setState({ smsDismissed: true })}
                    variant={'warning'}
                >
                    <div className={'fw-bold'}>
                        {hasNonApplication
                            ? (formatMessage({ id: 'sms_8_required.fd3a8ae8', defaultMessage: 'SMS 8 required' }))
                            : (formatMessage({ id: 'sms_required.e1197bd8', defaultMessage: 'SMS required' }))}
                    </div>
                    <div>
                        <FormattedMessage defaultMessage="This test includes Application questions. Students will need SMS to complete them." id="this_test_includes_application_questions.e4379ccd" />
                    </div>
                </Alert>
            )}

            {!hasQuestions && (
                <Alert className={'mb-0'} variant={'danger'}>
                    <FormattedMessage defaultMessage="This test has no questions. Add at least one question before saving." id="this_test_has_no_questions_add_at_least.00897578" />
                </Alert>
            )}

            {mode === 'edit' && (
                <TestSummaryBar richSummary showActualTime minutes={minutes} questionTree={questionTree} selectedQuestionIds={selectedSet} />
            )}

            <section>
                {renderSectionHeader(formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' }), () => editStep(EWizardStep.DETAILS))}
                <Row className={'border-bottom pb-3 gy-2'}>
                    <Col md={6} xs={12}>
                        <div className={'small text-muted'}>
                            <FormattedMessage defaultMessage="Test Name" id="test_name.77ea159f" />
                        </div>
                        <div>
                            {name || '—'}
                        </div>
                    </Col>
                    <Col md={6} xs={12}>
                        <div className={'small text-muted'}>
                            <FormattedMessage defaultMessage="Product" id="product.3ff5f045" />
                        </div>
                        <div>
                            {selectedProducts.length ? selectedProducts.map(product => product.Name).join(', ') : '—'}
                        </div>
                        {selectedCategory && (
                            <div className={'text-muted'}>
                                {selectedCategory.Name}
                            </div>
                        )}
                    </Col>
                    <Col md={6} xs={12}>
                        <div className={'small text-muted'}>
                            <FormattedMessage defaultMessage="Question Language" id="question_language.dfd461a5" />
                        </div>
                        <div>
                            {selectedLanguage?.Name ?? '—'}
                        </div>
                    </Col>
                </Row>
            </section>

            {smartBuildRun && (
                <section>
                    {renderSectionHeader(formatMessage({ id: 'smart_build.7cf76046', defaultMessage: 'Smart Build' }), () => editStep(EWizardStep.SMART_BUILD))}
                    <div
                        className={'rounded text-white p-3'}
                        style={{ background: 'linear-gradient(180deg, #5d4a9c 0%, #12263f 65%)' }}
                    >
                        {sbStatement}
                    </div>
                </section>
            )}

            <section>
                {renderSectionHeader(formatMessage({ id: 'test_settings.dfedf589', defaultMessage: 'Test Settings' }), () => editStep(EWizardStep.SETTINGS))}
                <div className={'bg-light rounded p-3'}>
                    <Row className={'gy-2'}>
                        {renderSetting(formatMessage({ id: 'passing_score.5fe03d6f', defaultMessage: 'Passing Score' }), `${minScore}%`)}
                        {renderSetting(formatMessage({ id: 'time_limit.d7ee27f0', defaultMessage: 'Time limit' }), `${minutes} ${formatMessage({ id: 'min.834408a4', defaultMessage: 'min' })}`)}
                        {renderSetting(formatMessage({ id: 'testing_mode.3727f585', defaultMessage: 'Testing Mode' }), modeLabel)}
                        {renderSetting(formatMessage({ id: 'question_delivery.579381e7', defaultMessage: 'Question delivery' }), deliveryLabel)}
                        {renderSetting(formatMessage({ id: 'question_order.8f29c4fa', defaultMessage: 'Question order' }), orderLabel)}
                    </Row>
                </div>
            </section>

            <section>
                {renderSectionHeader(formatMessage({ id: 'question_summary.f523b64e', defaultMessage: 'Question Summary' }), () => editStep(EWizardStep.QUESTIONS))}
                <Row className={'g-2 mb-3'}>
                    {renderStat(selectedQuestions, formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' }))}
                    {renderStat(deliveredCount, formatMessage({ id: 'delivered.17e143de', defaultMessage: 'Delivered' }))}
                    {renderStat(objectivesWithSelected.length, formatMessage({ id: 'objectives.de5845c5', defaultMessage: 'Objectives' }))}
                    {renderStat(sourceCount, formatMessage({ id: 'sources.9872e31e', defaultMessage: 'Sources' }))}
                </Row>

                {objectivesWithSelected.length > 0 && (
                    <>
                        <div className={'small text-muted mb-1'}>
                            <FormattedMessage defaultMessage="By Objective" id="by_objective.2f94216f" />
                        </div>
                        {objectivesWithSelected.map((objective, key) => (
                            <div className={'d-flex justify-content-between align-items-center border-bottom py-1'} key={key}>
                                <span>
                                    {objective.name}
                                </span>
                                <button
                                    onClick={() => {

                                        setProviderState({ focusObjectiveId: objective.id });
                                        editStep(EWizardStep.QUESTIONS);

                                    }}
                                    className={'btn btn-link p-0 text-decoration-none small'}
                                    type={'button'}
                                >
                                    <FormattedMessage defaultMessage="{count} selected questions" id="count_selected_questions.576a9bfc" values={{ count: objective.count }} />
                                </button>
                            </div>
                        ))}
                    </>
                )}
            </section>

            <section>
                <div className={'d-flex align-items-center gap-2 mb-2'}>
                    <h6 className={'fw-bold text-primary mb-0'}>
                        <FormattedMessage defaultMessage="Required licenses for assignment" id="required_licenses_for_assignment.c8f6eb0e" />
                    </h6>
                    <OverlayTrigger
                        overlay={(
                            <Tooltip>
                                <FormattedMessage defaultMessage="Assignment will be limited to Classrooms or Access Codes with at least one of these licenses." id="assignment_will_be_limited_to_classrooms.2a21936c" />
                            </Tooltip>
                        )}
                        placement={'top'}
                    >
                        <i className={'fa-light fa-circle-info text-muted'} />
                    </OverlayTrigger>
                </div>
                {licensesFailed ?
                    (
                        <div className={'bg-light rounded p-3 text-muted small'}>
                            <FormattedMessage defaultMessage="Licensing requirements are validated when this test is assigned to a classroom or access code." id="licensing_requirements_are_validated_whe.a2cae110" />
                        </div>
                    )
                    : licenses === null ?
                        (
                            <div className={'bg-light rounded p-3 text-muted small'}>
                                <span className={'spinner-border spinner-border-sm me-2'} role={'status'} />
                                <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                            </div>
                        )
                        : licenses.length === 0 ?
                            (
                                <div className={'bg-light rounded p-3 text-warning small'}>
                                    <FormattedMessage defaultMessage="No active license grants the selected programs" id="no_active_license_grants_the_selected_pr.2f3f1386" />
                                </div>
                            )
                            :
                            (
                                <div className={'bg-light rounded p-3'}>
                                    <Row className={'gy-2'}>
                                        {licenses.map((license, key) => (
                                            <Col key={key} md={6} xs={12}>
                                                <Row className={'gx-2'}>
                                                    <Col className={'text-muted small'} xs={5}>
                                                        <FormattedMessage defaultMessage="License" id="license.506a29aa" />
                                                    </Col>
                                                    <Col xs={7}>
                                                        {license.Name}
                                                    </Col>
                                                </Row>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
            </section>

        </div>
    );

};
